import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { CheckCircle2, RotateCcw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type AnswerKey = "sleep" | "body" | "demand";
type Answers = Partial<Record<AnswerKey, string>>;

interface Option {
  value: string;
  label: string;
  hint: string;
}

interface Question {
  key: AnswerKey;
  prompt: string;
  helper: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    key: "sleep",
    prompt: "How was your sleep last night?",
    helper: "Choose the description that best matches your recollection.",
    options: [
      { value: "rough", label: "Rough", hint: "Broken or short" },
      { value: "mixed", label: "Mixed", hint: "Some rest, some disruption" },
      { value: "restful", label: "Restful", hint: "Mostly restorative" },
    ],
  },
  {
    key: "body",
    prompt: "How does your body feel right now?",
    helper: "A present-moment description, not a diagnosis.",
    options: [
      { value: "heavy", label: "Heavy", hint: "Slower or more effortful" },
      { value: "neutral", label: "Neutral", hint: "No strong direction" },
      { value: "light", label: "Light", hint: "More ease than usual" },
    ],
  },
  {
    key: "demand",
    prompt: "How demanding has today felt so far?",
    helper: "Review what has already happened, not what comes next.",
    options: [
      { value: "quiet", label: "Quiet", hint: "Few demands so far" },
      { value: "varied", label: "Varied", hint: "A mixture of demands" },
      { value: "intense", label: "Intense", hint: "Sustained demands" },
    ],
  },
];

export interface CheckinSimulatorProps {
  variant?: "inline" | "page";
}

const CheckinSimulator = ({ variant = "page" }: CheckinSimulatorProps) => {
  const headingId = useId();
  const progressId = useId();
  const [answers, setAnswers] = useState<Answers>({});
  const [reviewed, setReviewed] = useState(false);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const reviewButtonRef = useRef<HTMLButtonElement | null>(null);
  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const answeredCount = useMemo(
    () => QUESTIONS.reduce((count, question) => count + (answers[question.key] ? 1 : 0), 0),
    [answers],
  );
  const complete = answeredCount === QUESTIONS.length;
  const progressPercent = Math.round((answeredCount / QUESTIONS.length) * 100);

  const selectedSummary = useMemo(
    () =>
      QUESTIONS.map((question) => {
        const selected = question.options.find(
          (option) => option.value === answers[question.key],
        );
        return { prompt: question.prompt, answer: selected?.label ?? "Not selected" };
      }),
    [answers],
  );

  const setOptionRef = useCallback(
    (questionKey: AnswerKey, value: string) =>
      (element: HTMLButtonElement | null) => {
        optionRefs.current[`${questionKey}:${value}`] = element;
      },
    [],
  );

  const setAnswer = (key: AnswerKey, value: string) => {
    setAnswers((current) => ({ ...current, [key]: value }));
    setReviewed(false);
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    questionIndex: number,
    optionIndex: number,
  ) => {
    const question = QUESTIONS[questionIndex];
    let nextIndex: number | null = null;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (optionIndex + 1) % question.options.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex =
          (optionIndex - 1 + question.options.length) % question.options.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = question.options.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextOption = question.options[nextIndex];
    setAnswer(question.key, nextOption.value);
    optionRefs.current[`${question.key}:${nextOption.value}`]?.focus();
  };

  useEffect(() => {
    if (complete && !reviewed) {
      const timeout = window.setTimeout(() => {
        reviewButtonRef.current?.focus({ preventScroll: true });
      }, 80);
      return () => window.clearTimeout(timeout);
    }
  }, [complete, reviewed]);

  const handleReview = () => {
    if (!complete) return;
    setReviewed(true);
    window.requestAnimationFrame(() => {
      summaryRef.current?.focus({ preventScroll: false });
    });
  };

  const handleReset = () => {
    setAnswers({});
    setReviewed(false);
    window.setTimeout(() => {
      const firstQuestion = QUESTIONS[0];
      const firstOption = firstQuestion.options[0];
      optionRefs.current[`${firstQuestion.key}:${firstOption.value}`]?.focus();
    }, 40);
  };

  return (
    <section
      aria-labelledby={headingId}
      data-demo-mode="local-retrospective"
      className={cn(
        "w-full",
        variant === "page" ? "mx-auto max-w-3xl" : "mx-auto max-w-4xl",
      )}
    >
      <div
        className={cn(
          "rounded-3xl border border-border/60 bg-card/80 shadow-premium-sm",
          variant === "page" ? "p-6 sm:p-10 lg:p-14" : "p-6 sm:p-8 lg:p-10",
        )}
      >
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-primary">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Fictional local reflection
        </div>

        <h2 id={headingId} className="mt-4 font-display text-3xl tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          Review three pieces of context. <span className="italic text-primary">Nothing is predicted.</span>
        </h2>
        <p className="mt-4 max-w-2xl text-[15px] leading-7 text-foreground/80 sm:text-base">
          These selections stay in browser memory for this page session. They are not submitted, stored, scored, used for outreach, or turned into a recommendation. The exercise shows only how self-reported context can be organized for retrospective review.
        </p>

        <div className="mt-8">
          <div className="mb-2 flex items-center justify-between text-[11px] uppercase tracking-[0.16em] text-foreground/70">
            <span id={progressId}>{complete ? "Ready to review" : `Question ${Math.min(answeredCount + 1, QUESTIONS.length)} of ${QUESTIONS.length}`}</span>
            <span>{answeredCount}/{QUESTIONS.length}</span>
          </div>
          <div
            role="progressbar"
            aria-labelledby={progressId}
            aria-valuemin={0}
            aria-valuemax={QUESTIONS.length}
            aria-valuenow={answeredCount}
            aria-valuetext={`${answeredCount} of ${QUESTIONS.length} answered`}
            className="h-2 overflow-hidden rounded-full bg-muted"
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="mt-8 space-y-8">
          {QUESTIONS.map((question, questionIndex) => {
            const selected = answers[question.key];
            const tabStop = selected ?? question.options[0].value;

            return (
              <fieldset key={question.key} className="space-y-3">
                <legend>
                  <span className="block font-display text-xl italic text-foreground sm:text-2xl">
                    {question.prompt}
                  </span>
                  <span className="mt-1 block text-[13px] text-foreground/70">
                    {question.helper}
                  </span>
                </legend>
                <div
                  role="radiogroup"
                  aria-label={question.prompt}
                  className="grid grid-cols-1 gap-2 sm:grid-cols-3"
                >
                  {question.options.map((option, optionIndex) => {
                    const isSelected = selected === option.value;
                    return (
                      <button
                        key={option.value}
                        ref={setOptionRef(question.key, option.value)}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={tabStop === option.value ? 0 : -1}
                        onClick={() => setAnswer(question.key, option.value)}
                        onKeyDown={(event) =>
                          handleOptionKeyDown(event, questionIndex, optionIndex)
                        }
                        className={cn(
                          "min-h-[72px] rounded-2xl border px-4 py-3 text-left outline-none transition-colors",
                          "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border/70 bg-background text-foreground hover:border-primary/60",
                        )}
                      >
                        <span className="block text-sm font-medium">{option.label}</span>
                        <span
                          className={cn(
                            "mt-1 block text-[11px] leading-4",
                            isSelected
                              ? "text-primary-foreground/85"
                              : "text-foreground/65",
                          )}
                        >
                          {option.hint}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button
            ref={reviewButtonRef}
            type="button"
            variant="premium"
            size="lg"
            onClick={handleReview}
            disabled={!complete}
            className="w-full sm:w-auto"
          >
            Review my selections
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleReset}
            className="w-full sm:w-auto"
          >
            <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
            Clear local selections
          </Button>
        </div>

        {reviewed && (
          <div
            ref={summaryRef}
            tabIndex={-1}
            role="status"
            aria-live="polite"
            className="mt-8 rounded-2xl border border-primary/20 bg-primary/5 p-5 outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-primary" aria-hidden="true" />
              <div>
                <h3 className="font-display text-xl text-foreground">Retrospective context summary</h3>
                <dl className="mt-4 space-y-3 text-sm">
                  {selectedSummary.map((item) => (
                    <div key={item.prompt} className="grid gap-1 sm:grid-cols-[1fr_auto] sm:gap-4">
                      <dt className="text-foreground/75">{item.prompt}</dt>
                      <dd className="font-medium text-foreground">{item.answer}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-5 text-sm leading-6 text-foreground/80">
                  This fictional summary does not predict future energy, infer a cause, assign risk, recommend an action, diagnose a condition, guide treatment, or provide emergency support. Nothing was submitted or stored.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CheckinSimulator;
